import {HttpClient, HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ClientService} from '../auth/client.service';

export type ShineAiReportGrain = 'Day' | 'Week' | 'Month';
export type ProductCardAnalyticsReportGrain = 'Day' | 'Week' | 'Month';
export type ProductCardAnalyticsSortKey = 'clicks' | 'viewed';

export interface ProductCardAnalyticsReportRequest {
  startDate: string;
  endDate: string;
  grain: ProductCardAnalyticsReportGrain;
  chatbotId?: string;
  assistantId?: string;
  productCardType?: string;
  presentation?: string;
  productSearch?: string;
  eventSource?: string;
}

export interface ShineAiConversationReport {
  startDate: string;
  endDate: string;
  grain: ShineAiReportGrain | number;
  totalConversations: number;
  totalMessages: number;
  totalConversationMinutes: number;
  averageConversationMinutes: number;
  averageMessagesPerConversation: number;
  thumbsUp: number;
  thumbsDown: number;
  feedbackTotal: number;
  positiveFeedbackRate: number;
  periods: ShineAiConversationPeriodReport[];
  chatbots: ShineAiConversationChatbotReport[];
  categories: ShineAiConversationCategoryReport[];
  functionCalls?: ShineAiFunctionCallReport | null;
  functionCallReportingStatus?: 'available' | 'unavailable';
}

export interface ShineAiFunctionCallReport {
  conversationsWithActions?: number;
  callsWithKnownOutcome?: number;
  outcomeCoveragePercent?: number;
  types?: ShineAiFunctionCallDimension[];
  outcomes?: ShineAiFunctionCallDimension[];
  chatbots?: {chatbotId: string | null; report: ShineAiFunctionCallReport}[];
  totalCalls: number;
  conversationsWithCalls: number;
  functions: {functionName: string; calls: number; conversations: number; outcomes?: ShineAiFunctionCallDimension[]}[];
  periods: {periodStart: string; periodLabel: string; calls: number; conversations: number}[];
}

export interface ShineAiFunctionCallDimension {
  key: string;
  calls: number;
  conversations: number;
}

export interface ShineAiConversationPeriodReport {
  periodStart: string;
  periodLabel: string;
  year: number;
  month: number | null;
  conversations: number;
  messages: number;
  conversationMinutes: number;
  thumbsUp: number;
  thumbsDown: number;
  feedbackTotal: number;
  positiveFeedbackRate: number;
}

export interface ShineAiConversationChatbotReport {
  chatbotId: string | null;
  conversations: number;
  messages: number;
  conversationMinutes: number;
  thumbsUp: number;
  thumbsDown: number;
  feedbackTotal: number;
  positiveFeedbackRate: number;
}

export interface ShineAiConversationCategoryReport {
  category: string;
  conversations: number;
  messages: number;
  conversationMinutes: number;
  thumbsUp: number;
  thumbsDown: number;
  feedbackTotal: number;
  positiveFeedbackRate: number;
}

export interface ProductCardAnalyticsReport {
  startDate: string;
  endDate: string;
  grain: ProductCardAnalyticsReportGrain | number;
  filters: ProductCardAnalyticsFilters;
  totals: ProductCardAnalyticsMetrics;
  rateDefinitions: ProductCardAnalyticsRateDefinitions;
  trend: ProductCardAnalyticsTrendPoint[];
  chatbots: ProductCardAnalyticsBreakdown[];
  assistants: ProductCardAnalyticsBreakdown[];
  productCardTypes: ProductCardAnalyticsBreakdown[];
  presentations: ProductCardAnalyticsBreakdown[];
  inputPaths?: ProductCardAnalyticsBreakdown[];
  authorities?: ProductCardAnalyticsBreakdown[];
  sources?: ProductCardAnalyticsBreakdown[];
  products: ProductCardAnalyticsProductMetric[];
}

export interface ProductCardAnalyticsFilters {
  chatbotId: string | null;
  assistantId: string | null;
  productCardType: string | null;
  presentation: string | null;
  productSearchHash: string | null;
  eventSource: string | null;
}

export interface ProductCardAnalyticsRateDefinitions {
  viewRate: string;
  clickThroughRateByDisplay: string;
  clickThroughRateByView: string;
}

export interface ProductCardAnalyticsMetrics {
  displayed: number;
  viewed: number;
  hovered: number;
  clicks: number;
  viewRate: number;
  clickThroughRateByDisplay: number;
  clickThroughRateByView: number;
  uniqueProducts: number;
  productCardConversations: number;
}

export interface ProductCardAnalyticsTrendPoint {
  periodStart: string;
  periodLabel: string;
  metrics: ProductCardAnalyticsMetrics;
}

export interface ProductCardAnalyticsBreakdown {
  key: string | null;
  label?: string | null;
  metrics: ProductCardAnalyticsMetrics;
}

export interface ProductCardAnalyticsProductMetric {
  productKey: string;
  productCardId: string | null;
  productName?: string | null;
  name?: string | null;
  productNameHash: string | null;
  sku?: string | null;
  skuHash: string | null;
  productUrlHash: string | null;
  label?: string | null;
  metrics: ProductCardAnalyticsMetrics;
}

@Injectable({
  providedIn: 'root'
})
export class ShineAiReportsService {
  private readonly http = inject(HttpClient);
  private readonly clientService = inject(ClientService);

  getConversationSummary(
    startDate: string,
    endDate: string,
    grain: ShineAiReportGrain
  ): Observable<ShineAiConversationReport> {
    const clientId = this.clientService.client?.id ?? '';
    const params = new HttpParams()
      .set('start', startDate)
      .set('end', endDate)
      .set('grain', grain);

    return this.http.get<ShineAiConversationReport>(
      `${environment.aiBaseServerUrl}${clientId}/reports/shine-ai/conversation-summary`,
      {params}
    );
  }

  getProductCardAnalytics(request: ProductCardAnalyticsReportRequest): Observable<ProductCardAnalyticsReport> {
    const clientId = this.clientService.client?.id ?? '';
    let params = new HttpParams()
      .set('start', request.startDate)
      .set('end', request.endDate)
      .set('grain', request.grain);

    params = this.setOptionalParam(params, 'chatbotId', request.chatbotId);
    params = this.setOptionalParam(params, 'assistantId', request.assistantId);
    params = this.setOptionalParam(params, 'productCardType', request.productCardType);
    params = this.setOptionalParam(params, 'presentation', request.presentation);
    params = this.setOptionalParam(params, 'productSearch', request.productSearch);
    params = this.setOptionalParam(params, 'eventSource', request.eventSource);

    return this.http.get<ProductCardAnalyticsReport>(
      `${environment.aiBaseServerUrl}${clientId}/reports/shine-ai/product-card-analytics`,
      {params}
    );
  }

  private setOptionalParam(params: HttpParams, key: string, value: string | undefined): HttpParams {
    const trimmed = `${value ?? ''}`.trim();
    return trimmed ? params.set(key, trimmed) : params;
  }
}
